/* oxlint-disable react-compiler/refs -- won't fix for this legacy file */
import {
  Children,
  cloneElement,
  type RefObject,
  type ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import styled from 'styled-components'
import { Body } from '@shipt/design-system-typography'
import { shadow2 } from '@/features/shared/styles/effects'
import { getKey } from '@/features/shared/constants/keyCodes'
import {
  isKeyCode,
  isUpDownKeyEvent,
  isButtonActionEvent,
  isStandardCharacterKeyEvent,
} from '@/features/shared/utils/keyEvents'
import { type OptionProps } from '@/features/shared/components/Dropdown/Option'

type OptionType = { props: OptionProps<string> }

type Props = {
  className?: string
  isOpen: boolean
  onChange: (value: string) => void
  onClose: () => void
  value: string | number
  buttonRef: RefObject<HTMLButtonElement | null>
  isScrollable: boolean | undefined
  children: ReactNode
}

export const DesktopDropdown = ({
  className,
  isOpen = false,
  onChange,
  onClose,
  value = '',
  buttonRef,
  isScrollable = false,
  children,
}: Props) => {
  const containerRef = useRef<HTMLUListElement | null>(null)

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const searchTermRef = useRef('')

  const optionRefs = useRef<Record<string, HTMLElement>>({})

  const [focusedOptionIndex, setFocusedOptionIndex] = useState(0)
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions -- known type mismatch
  const listItems = Children.toArray(children) as OptionType[]

  const [activeDescendant, setActiveDescendant] = useState<string | undefined>(undefined)
  const [currentValue, setCurrentValue] = useState(value)

  const handleOptionClick = (_event: React.MouseEvent, newValue: string) => {
    setCurrentValue(newValue)
    onChange(newValue)
    onClose()
  }

  const handleOptionKeyPress = (event: React.KeyboardEvent, newValue: string) => {
    if (isButtonActionEvent(event)) {
      event.preventDefault()
      setCurrentValue(newValue)
      onChange(newValue)
      onClose()
    }
  }

  const handleOptionMouseOver = (_event: React.MouseEvent, _newValue: string, index: number) => {
    // emulate native select by focusing element on hover
    // oxlint-disable-next-line react-compiler/immutability -- file is too legacy to fix these react compiler issues
    setNewFocusedOptionIndex(index)
  }

  const handleGlobalKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return
    if (isKeyCode(e, 'Tab')) {
      // Don't allow tabbing through options in dropdown
      // https://www.w3.org/TR/wai-aria-practices/#listbox_kbd_interaction
      e.preventDefault()
    } else if (isButtonActionEvent(e)) {
      e.preventDefault()
      setCurrentValue(listItems[focusedOptionIndex]?.props.value || '')
      onChange(listItems[focusedOptionIndex]?.props.value || '')
      onClose()
    } else if (isUpDownKeyEvent(e)) {
      // prevent page scroll
      e.preventDefault()
      handleArrowKeyNav(e)
    } else if (isKeyCode(e, 'Home') || isKeyCode(e, 'End')) {
      // prevent page scroll
      e.preventDefault()
      handleHomeAndEnd(e)
    } else if (isKeyCode(e, 'Escape')) {
      // prevent closing PDP modal
      e.stopPropagation()
      onClose()
    } else if (isStandardCharacterKeyEvent(e)) {
      e.preventDefault()
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = setTimeout(() => {
        searchTermRef.current = ''
      }, 500)
      const searchTerm = searchTermRef.current + getKey(e)
      handleSearch(searchTerm)
    }
  }

  const handleSearch = (term: string) => {
    const foundIndex = _findNextIndex(listItems, term, focusedOptionIndex + 1)

    if (foundIndex > -1) {
      setNewFocusedOptionIndex(foundIndex)
    }
  }

  const handleHomeAndEnd = (e: React.KeyboardEvent) => {
    const childrenCount = listItems.length
    if (isKeyCode(e, 'Home')) {
      setNewFocusedOptionIndex(0)
    } else {
      setNewFocusedOptionIndex(childrenCount - 1)
    }
  }

  const handleArrowKeyNav = (e: React.KeyboardEvent) => {
    const { length: childrenCount } = listItems
    if (isKeyCode(e, 'ArrowUp') && focusedOptionIndex > 0) {
      setNewFocusedOptionIndex(focusedOptionIndex - 1)
    } else if (isKeyCode(e, 'ArrowDown') && focusedOptionIndex < childrenCount - 1) {
      setNewFocusedOptionIndex(focusedOptionIndex + 1)
    }
  }

  const setNewFocusedOptionIndex = useCallback(
    (newFocusedOptionIndex: number) => {
      const offset = newFocusedOptionIndex - focusedOptionIndex > 0 ? 1 : -1
      const nextOption = listItems[newFocusedOptionIndex]

      // Recurse early until we hit the end of the Options or encounter a non-disabled Option
      // The newFocusedOptionIndex !== focusedOptionIndex check is necessary to prevent
      // infinite recursion when focus is called on the currently focused Option
      if (!nextOption) return
      else if (nextOption.props.disabled && newFocusedOptionIndex !== focusedOptionIndex) {
        setNewFocusedOptionIndex(newFocusedOptionIndex + offset)
        return
      }
      // If we made it here, we are done recursing (or didn't have to) and are on a valid, enabled Option
      setCurrentValue(listItems[newFocusedOptionIndex]?.props.value || '')
      setActiveDescendant(`dropdown_option_${newFocusedOptionIndex}`)
      setFocusedOptionIndex(newFocusedOptionIndex)
      const optionRef = optionRefs.current[`${newFocusedOptionIndex}`]

      // If open, scrollable, and the contents actually require scrollability
      if (
        isOpen &&
        isScrollable &&
        containerRef.current &&
        containerRef.current.scrollHeight > containerRef.current.clientHeight &&
        optionRef !== undefined
      ) {
        // scrollBottom is the pixel value for number of pixels scrolled from top of container
        const scrollBottom = containerRef.current.clientHeight + containerRef.current.scrollTop
        // elementBottom is the pixel value for number of pixels the bottom of the focused element is
        // from the top of the scroll container
        const elementBottom = optionRef.offsetTop + optionRef.offsetHeight
        if (elementBottom > scrollBottom) {
          // If the bottom of the newly focused element is vertically below the current vertical scroll position
          // then set the scrolling container's scrollTop offset to align the bottom of the focused element
          // with the bottom of the scrolling container (scroll down to align new option bottom to bottom of scroll area)
          containerRef.current.scrollTop = elementBottom - containerRef.current.clientHeight
        } else if (optionRef.offsetTop < containerRef.current.scrollTop) {
          // If the top of the newly focused element is vertically above the current vertical scroll position
          // then set the scrolling container's scrollTop offset to align the top of the focused element
          // with the top of the scrolling container (scroll up to align new option top to top of scroll area)
          containerRef.current.scrollTop = optionRef.offsetTop
        }
      }
    },
    [focusedOptionIndex, isScrollable, isOpen, listItems, optionRefs],
  )

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) {
      const foundIndex = listItems.findIndex((option) => option.props.value === currentValue)
      if (foundIndex > -1) {
        setNewFocusedOptionIndex(foundIndex)
      }
    }
  }

  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (e: MouseEvent) => {
        if (!containerRef.current || !buttonRef.current) return
        // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions -- dom handler type assertion
        const target = e.target as HTMLElement
        if (!buttonRef.current.contains(target) && !containerRef.current.contains(target)) {
          onClose()
        }
      }
      document.addEventListener('mousedown', handleClickOutside)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [buttonRef, isOpen, onClose])

  const getDropdownListRef = useCallback((node: HTMLUListElement | null) => {
    if (!node) return
    containerRef.current = node
    node.focus()
  }, [])

  return isOpen ? (
    <DropdownList
      as="ul"
      role="listbox"
      aria-activedescendant={activeDescendant}
      tabIndex={0}
      isScrollable={isScrollable}
      className={className}
      onKeyDown={handleGlobalKeyDown}
      ref={getDropdownListRef}
      size="md"
    >
      {listItems.map((option, index) =>
        // @ts-expect-error ts-migrate(2769) No overload matches this call.
        cloneElement(option, {
          onClick: (e: React.MouseEvent) => {
            if (option.props.disabled) return
            handleOptionClick(e, option.props.value)
          },
          onKeyPress: (e: React.KeyboardEvent) => {
            if (option.props.disabled) return
            handleOptionKeyPress(e, option.props.value)
          },
          onMouseOver: (e: React.MouseEvent) => {
            if (option.props.disabled) return
            handleOptionMouseOver(e, option.props.value, index)
          },
          focusedIndex: focusedOptionIndex,
          index,
          id: `dropdown_option_${index}`,
          innerRef: (node: HTMLElement) => {
            optionRefs.current[`${index}`] = node
          },
        }),
      )}
    </DropdownList>
  ) : null
}

const _itemStartsWithTerm = (item: OptionType, term: string) => {
  const val = '' + (item.props.label || item.props.value || '')
  return val.toUpperCase().startsWith(term)
}
const _findNextIndex = (items: OptionType[], term: string, startIndex: number) => {
  let foundIndex = items.findIndex(
    (item, index) => index >= startIndex && _itemStartsWithTerm(item, term),
  )
  if (foundIndex < 0) {
    // search again from beginning of array
    foundIndex = items.findIndex((item) => _itemStartsWithTerm(item, term))
  }
  return foundIndex
}

export const DropdownList = styled(Body)<{ isScrollable: boolean }>`
  list-style-type: none;
  position: absolute;
  width: 100%;
  padding: 0;
  margin: 0;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.color.background.base.neutral.subtle_00};
  ${shadow2}
  overflow: ${({ isScrollable }) => isScrollable && 'auto'};
  max-height: ${({ isScrollable }) => isScrollable && '11rem'};
  z-index: 5;
`
